import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { Bell, Calendar, Clock, Globe, Info, Mail, User, Circle, Trash2, Check, Loader2 } from "lucide-react";
import { getAppointmentsByIdQueryOptions } from "../api/queries/appointments";
import { cn } from "@workspace/web-ui/lib/utils";
import { FormattedEncounter, Session } from "../types";
import { UrgentCareTaskButtons } from "~/features/dashboard/components/tasks/urgent-care-task";
import { useDeleteTaskMutation } from "../../../api/tasks/mutation";
import { encountersKeys } from "../api/queries";
import { getApiProgram } from "~/lib/utils";
import { useState, useEffect, type CSSProperties } from "react";
import { AppointmentStatusCard } from "../../quick-actions/components/cards/appointment-status";
import { toast } from "sonner";
import { useUpdateAppointmentMutation } from "../../quick-actions/api/mutations/appointment";
import { quickActionsKeys } from "../../quick-actions/api/queries";
import { RecommendedTask } from "./recommended-task";
import { PatientDetailsConditional } from "~/features/dashboard/components/patient/patient-details-conditional";
import { useSendActionNeededMutation } from "../../patient-logs/api/mutations";
import { NotesContainer } from "~/features/dashboard/components/notes";
import { ResolveToggle } from "~/features/dashboard/components/resolve-toggle";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
import { Image } from "@workspace/web-ui/components/image";
// import { QUESTION_LABELS } from "../../quick-actions/components/drawers/consultation-modal";
// import { TAG_STYLES } from "@workspace/web-ui/lib/encounter";
// import { useState } from "react";

// const COLORS = {
//     TEXT_PRIMARY: "#2D2F35",
//     TEXT_SECONDARY: "#667085",
// } as const;

interface InfoRowProps {
    icon: typeof User | typeof Mail | typeof Calendar | typeof Globe | typeof Bell;
    value: string;
    title?: string;
    testId: string;
}

const InfoRow = ({ icon: Icon, value, title, testId }: InfoRowProps) => {
    if (title) {
        return (
            <div className="grid grid-cols-2 w-[70%] gap-3 items-start" data-testid={testId}>
                {/* <Icon className="w-4 h-4 text-[#7F8493]" /> */}
                <span className="text-sm font-medium text-[##202020CC]">{title}</span>
                <span className="text-sm font-normal text-[##202020CC]">{value}</span>
            </div>
        );
    }

    return (
        <div className="flex gap-3 items-center" data-testid={testId}>
            {Icon && <Icon className="w-4 h-4 text-[#7F8493]" />}
            <span className="text-sm font-normal text-[##202020CC]">{value}</span>
        </div>
    );
};

const formatDate = (dateString?: string, format: "datetime" | "date" = "datetime"): string => {
    if (!dateString) return "N/A";

    try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return "N/A";
        }
        if (format === "datetime") {
            return date.toLocaleString();
        }
        return date.toLocaleDateString();
    } catch {
        return "N/A";
    }
};

const isHappeningNow = (dateString?: string): string => {
    if (!dateString) return "Unknown";

    try {
        const appointmentTime = new Date(dateString);
        if (Number.isNaN(appointmentTime.getTime())) {
            return "Unknown";
        }

        const now = new Date();
        const timeDiff = appointmentTime.getTime() - now.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);

        if (Math.abs(hoursDiff) < 1) {
            return "Happening now";
        }

        if (appointmentTime > now) {
            return "Upcoming";
        }

        return "Past";
    } catch {
        return "Unknown";
    }
};

// Skeleton loader for appointment details
function AppointmentDetailsSkeleton() {
    return (
        <div className="flex flex-col w-full h-full bg-white rounded-[24px] border border-[#0404041A] overflow-auto gap-4 sm:gap-5 p-4 sm:py-4 sm:px-4 animate-pulse">
            <div className="h-6 w-1/2 bg-gray-200 rounded mb-2" />
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between">
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                    <div className="h-8 w-2/3 bg-gray-200 rounded mb-1" />
                    <div className="flex gap-2">
                        <div className="h-4 w-20 bg-gray-200 rounded" />
                        <div className="h-4 w-12 bg-gray-200 rounded" />
                        <div className="h-4 w-10 bg-gray-200 rounded" />
                    </div>
                </div>
                <div className="h-10 w-32 bg-gray-200 rounded" />
            </div>
            <div className="flex flex-col gap-2 mt-2 sm:mt-5">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-4 w-full bg-gray-200 rounded" />
                ))}
            </div>
            <div className="h-4 w-1/3 bg-gray-200 rounded mt-2" />
            <div className="h-12 w-full bg-gray-200 rounded mt-2" />
            <div className="h-10 w-1/2 bg-gray-200 rounded mt-2" />
        </div>
    );
}

// This component contains the actual appointment details content
export const AppointmentDetailsContent = ({ encounter }: { encounter: FormattedEncounter }) => {
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const queryClient = useQueryClient();
    const [taskId, setTaskId] = useState<string | null>(null);
    const [localTag, setLocalTag] = useState(encounter.tag);
    // const [appointmentHeld, setAppointmentHeld] = useState<boolean | null>(null)

    useEffect(() => {
        setLocalTag(encounter.tag);
    }, [encounter.tag]);





    const { data: appointment, isLoading } = useQuery(getAppointmentsByIdQueryOptions({
        id: encounter.id ?? "",
    }));

    const UpdateAppointmentMutation = useUpdateAppointmentMutation({
        id: encounter.id ?? ""
    });

    const { mutate: sendActionNeeded, isPending: sendActionpending } = useSendActionNeededMutation();

    const infoRows = [
        {
            icon: User,
            title: "Name",
            value: appointment?.patient.name,
            testId: 'patient-name'
        },
        {
            icon: User,
            title: "Gender",
            value: appointment?.patient.gender,
            testId: 'patient-gender'
        },
        {
            icon: User,
            title: "Age",
            value: appointment?.patient.age != null ? String(appointment.patient.age) : "",
            testId: 'patient-age'
        },
        {
            icon: Calendar,
            title: "Meeting Time",
            value: `${formatDate(appointment?.meetingStartingTime ?? "", "datetime")}`,
            testId: 'appointment-datetime'
        },
        {
            icon: Globe,
            title: "Location",
            value: appointment?.timezone,
            testId: 'appointment-location'
        },
        {
            icon: Mail,
            title: "Email",
            value: appointment?.patient.email,
            testId: 'patient-email'
        },
        {
            icon: Bell,
            title: "Status",
            value: isHappeningNow(appointment?.meetingStartingTime ?? ""),
            testId: 'appointment-status'
        }
    ];

    const weightLossInfoRows = [
        {
            icon: User,
            value: appointment?.patient.name,
            testId: "patient-name"
        },
        {
            icon: Calendar,
            value: `${formatDate(appointment?.meetingStartingTime ?? "", "datetime")}`,
            testId: "appointment-datetime"
        },
        {
            icon: Globe,
            value: appointment?.timezone,
            testId: "appointment-location"
        },
        {
            icon: Mail,
            value: appointment?.patient.email,
            testId: "patient-email"
        },
        {
            icon: Bell,
            value: isHappeningNow(appointment?.meetingStartingTime ?? ""),
            testId: "appointment-status"
        }
    ];

    const primaryDiagnosis = (encounter as any)?.session?.diagnosis?.[0];

    const reportedSymptom = (encounter as any)?.session?.reported_symptom ??
        (encounter as any)?.session?.reported_symptoms ?? null;

    const diagnosisMeds = Array.isArray(primaryDiagnosis?.treatment_page?.medications)
        ? primaryDiagnosis.treatment_page.medications
        : [];

    const encounterId = (encounter as any)?.id ?? (encounter as any)?.session?.id;

    const tasks =
        (encounter as any)?.recommendedTasks ??
        (encounter as any)?.recommended_tasks ??
        [];

    const deleteTaskMutation = useDeleteTaskMutation({
        task_id: taskId ?? "",
        encounter_id: encounterId,
    });

    const handleDeleteTask = (id: string) => {
        if (!id) return;
        setTaskId(id);
        deleteTaskMutation.mutate();
        queryClient.invalidateQueries({ queryKey: encountersKeys.encounters({ program: getApiProgram(program) }) });
    };

    const hasAssignedActions = Array.isArray(tasks) && tasks.length > 0;

    const humanize = (value?: string) =>
        value ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Task";

    const getTaskTitle = (task: any) => {
        if (task?.type === "medication_payment") return "Prescribed Medication";
        if (task?.type === "appointment_followup") return "Pay for Appointment";
        return humanize(task?.type);
    };

    const formatTaskDate = (value?: string | number) => {
        if (!value) return "-";
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return "-";
        return dt.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    const handleMarkAppointmentStatus = async (status: 'attended' | 'cancelled') => {
        const meetingStatus = status === "cancelled" ? "not-held" : "held";
        try {
            await UpdateAppointmentMutation.mutateAsync(
                { status },
                {
                    onSuccess(data) {
                        if (data.status === "success") {
                            // Update localTag immediately so status shows correct color
                            setLocalTag(status === "attended" ? "Held" : "Not Held");
                            toast.success(`Appointment marked as ${meetingStatus}`, {
                                style: {
                                    background: "#6EE7B7",
                                    color: "#FFFFFF",
                                    borderColor: "#6EE7B7",
                                    "& svg": {
                                        color: "#FFFFFF",
                                    },
                                } as CSSProperties,
                            });
                        }
                        queryClient.invalidateQueries({ queryKey: quickActionsKeys.appointmentById(encounter.id ?? "") });
                        queryClient.invalidateQueries({ queryKey: encountersKeys.all });
                    },
                    onError() {
                        toast.error(`An error occurred while updating appointment status as ${meetingStatus}`);
                    },
                }
            );
        } catch (error) {
            console.log("error updating appointment status", error);
        }
    };

    // Show skeleton while loading
    if (isLoading) {
        return <AppointmentDetailsSkeleton />;
    }

    return (

        <>
            {program === "weight-loss" && (
                <div className="flex flex-col sm:w-full h-full bg-[#FFFFFF] rounded-[24px] border border-[#0404041A] overflow-auto gap-4 sm:gap-5 p-4 sm:py-[16px] sm:px-[32px]">
                    <div className="flex items-center gap-2">
                        <div
                            className={cn(
                                "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit border",
                                localTag?.toLowerCase() === "held"
                                    ? "text-[#16A34A] bg-[#16A34A26] border-[#16A34A1A]"
                                    : localTag?.toLowerCase() === "not held"
                                        ? "text-[#DC2626] bg-[#DC262626] border-[#DC26261A]"
                                        : "text-[#6B7280] bg-[#F3F4F6] border-[#D1D5DB]"
                            )}
                            data-testid="consultation-status"
                        >
                            <Calendar
                                className={cn(
                                    "size-4",
                                    localTag?.toLowerCase() === "held"
                                        ? "text-[#16A34A]"
                                        : localTag?.toLowerCase() === "not held"
                                            ? "text-[#DC2626]"
                                            : "text-[#6B7280]"
                                )}
                            />
                            <span className="text-xs font-medium">
                                {localTag
                                    ? localTag.charAt(0).toUpperCase() + localTag.slice(1)
                                    : "Not Held"}
                            </span>
                        </div>
                        {/* <ResolveToggle encounterId={encounter.encounterId} recommendedTasks={encounter.recommendedTasks} /> */}
                        {encounter.recommendedTasks && encounter.recommendedTasks.length > 0 ? (
                            <div
                                className={cn(
                                    "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                                    "text-[#7B238D] bg-[#7B238D26] border"
                                )}
                                data-testid="consultation-status"
                            >
                                <Clock className="size-4 text-[#7B238D]" />
                                <span className="text-xs font-medium">Resolved</span>
                            </div>
                        ) : localTag?.toLowerCase() === "not held" ? null : (
                            <div
                                className={cn(
                                    "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                                    "text-[#FFA726] bg-[#FFF5E6]"
                                )}
                                data-testid="consultation-status"
                            >
                                <Clock className="size-4 text-[#FFA726]" />
                                <span className="text-xs font-medium">Action needed</span>
                            </div>
                        )}
                    </div>

                    {/* <h2 className="text-lg sm:text-xl font-medium">{appointment?.description}</h2> */}

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between w-full">
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                            <div className="flex items-center gap-2">
                                <h2 className="text-2xl sm:text-3xl font-bold truncate max-w-full">{appointment?.patient?.name}</h2>
                                {encounter.patient.medical_plan?.payment_status === "paid" &&
                                    encounter.patient.glp1_guide_accepted &&
                                    encounter.patient.identity_verified &&
                                    encounter.patient.supplementary_health_assessment &&
                                    Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                        <div>
                                            <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                        </div>
                                    )}
                            </div>

                            <div className="flex flex-wrap items-center gap-y-1 gap-x-2">
                                {/* <span className="text-sm" style={{ color: '#2D2F35' }}>
                                    Weight Loss Program
                                </span>
                                <span className="hidden sm:inline mx-1" style={{ color: '#667085' }}>•</span> */}
                                <span className="text-sm" style={{ color: '#2D2F35' }}>
                                    {appointment?.patient.age} years
                                </span>
                                <span className="hidden sm:inline mx-1" style={{ color: '#667085' }}>•</span>
                                <span className="text-sm" style={{ color: '#2D2F35' }}>
                                    {appointment?.patient.gender}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 mt-2 sm:mt-5 w-full">
                        {weightLossInfoRows.map(({ icon, value, testId }) => (
                            <InfoRow key={testId} icon={icon} value={value ?? ""} testId={testId} />
                        ))}
                    </div>

                    <h2 className="text-xs font-normal text-[#202020CC]">Created {appointment?.meetingCreatedAt}</h2>
                    <PatientDetailsConditional encounter={encounter} program={program} />

                    {new Date(`${encounter.date} ${encounter.time}`) > new Date() && (
                        <div className="flex flex-col md:flex-row items-center gap-3 p-4 w-full bg-[#EBF0FB] rounded-lg">
                            <Info className="size-6 text-[#4272DD]" />
                            {encounter.status === "happening" ? (
                                <p className="text-sm font-normal text-[#202020CC]">This Appointment is live.</p>
                            ) : (
                                <p className="text-xs font-normal text-[#202020CC] whitespace-normal">
                                    This appointment is scheduled for {`${encounter.date}`}. Please ensure you're available 5 minutes before the start time.
                                </p>
                            )}

                            {encounter.status === "happening" && (
                                <Link
                                    className={cn(
                                        buttonVariants({ variant: "outline" }),
                                        "bg-transparent w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border-none py-3 px-4 hover:bg-[#4272DD] hover:text-white"
                                    )}
                                    to="/dashboard/$program/patients/patient/$id"
                                    params={{
                                        id: encounter?.patient.id || "",
                                        program
                                    }}
                                    search={{
                                        name: encounter?.patient.name.replace(/\s+/g, "") || "",
                                        sessionId: encounter?.session?.session_id || ""
                                    }}
                                >
                                    View Patient Log
                                </Link>
                            )}

                            <a
                                href={appointment?.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(
                                    buttonVariants({ variant: "default" }),
                                    "bg-[#4272DD] rounded-lg w-full sm:w-auto",
                                    encounter.status !== "happening" ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : ""
                                )}
                            >
                                Join meeting
                            </a>
                        </div>
                    )}

                    <div className="flex gap-4 w-full flex-wrap">
                        {(encounter.status === "happening" || encounter.status === "past" || encounter.status === "cancelled") && (
                            <div className="flex flex-col w-full">
                                <AppointmentStatusCard
                                    status={encounter.status}
                                    encounter={encounter}
                                    onStatusChange={handleMarkAppointmentStatus}
                                />
                            </div>
                        )}
                    </div>

                    {/* Notes Section */}
                    {["happening", "past", "completed", "attended"].includes(encounter.status) &&
                        encounter.patient?.medical_plan?.payment_status === "paid" &&
                        encounter.patient?.glp1_guide_accepted &&
                        encounter.patient?.identity_verified &&
                        encounter.patient?.supplementary_health_assessment &&
                        Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                            <NotesContainer
                                encounterId={encounter.encounterId}
                                patientId={encounter.patient.id}
                            />
                        )
                    }

                    {["happening", "past", "completed", "attended"].includes(encounter.status) && (
                        <>
                            {!(encounter.patient?.medical_plan?.payment_status === "paid" &&
                                encounter.patient?.glp1_guide_accepted &&
                                encounter.patient?.identity_verified &&
                                encounter.patient?.supplementary_health_assessment &&
                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0) && (
                                    <div className="flex items-center bg-[#F0F5FF] rounded-lg px-4 py-2 justify-between w-full">
                                        <p className="text-sm text-[#1A202C]">Patient must be verified</p>
                                        <Button
                                            className="button-primary !bg-[#4272DD] h-[44px] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                            onClick={async () => {
                                                try {
                                                    await sendActionNeeded({ patient_id: encounter.patient.id });
                                                    toast.success("Reminder sent to patient to complete verification.");
                                                } catch {
                                                    toast.error("Failed to send reminder.");
                                                }
                                            }}
                                            disabled={sendActionpending}
                                        >
                                            {sendActionpending && (
                                                <svg
                                                    className="animate-spin h-4 w-4 mr-2 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                    ></path>
                                                </svg>
                                            )}
                                            Send a Reminder
                                        </Button>
                                    </div>
                                )}
                            <RecommendedTask
                                encounterId={encounter.encounterId}
                                patientId={encounter.patient.id}
                                showAddButton={
                                    encounter.patient?.medical_plan?.payment_status === "paid" &&
                                    encounter.patient?.glp1_guide_accepted &&
                                    encounter.patient?.identity_verified &&
                                    encounter.patient?.supplementary_health_assessment &&
                                    Object.keys(encounter.patient.supplementary_health_assessment).length > 0
                                }
                            />
                        </>
                    )}
                </div>
            )}

            {program === "urgent-care" && (
                <div className="flex flex-col sm:w-full h-full bg-[#FFFFFF] rounded-[24px] border border-[#0404041A] overflow-auto gap-4 sm:gap-5 p-4 sm:py-[16px] sm:px-[32px]">
                    {/* Appointment description/title */}
                    {/* <h2 className="text-lg sm:text-xl font-medium">{appointment?.description}</h2> */}

                    <div className="flex items-center justify-between gap-2">
                        <div
                            className={cn(
                                "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit border",
                                localTag?.toLowerCase() === "held"
                                    ? "text-[#16A34A] bg-[#16A34A26]"
                                    : localTag?.toLowerCase() === "not held"
                                        ? "text-[#DC2626] bg-[#DC262626]"
                                        : "text-[#7F8493] bg-[#EDF0F3]" // fallback style
                            )}
                            data-testid="consultation-status"
                        >
                            <Calendar className={cn(
                                "size-4",
                                localTag?.toLowerCase() === "held"
                                    ? "text-[#16A34A]"
                                    : localTag?.toLowerCase() === "not held"
                                        ? "text-[#DC2626]"
                                        : "text-[#7F8493]" // fallback icon color
                            )} />
                            <span className="text-xs font-medium">{localTag || "Not Held"}</span>
                        </div>
                        <ResolveToggle encounterId={encounter.encounterId} recommendedTasks={encounter.recommendedTasks} />
                    </div>

                    {/* Patient info section */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between w-full">
                        <div className="flex flex-col gap-2 w-full">
                            <div className="flex items-center gap-2">
                                <h2 className="text-2xl sm:text-3xl font-bold truncate max-w-full">{appointment?.patient?.name}</h2>
                                {
                                    encounter.patient.urgent_care_onboarded && encounter.patient.identity_verified && (
                                        <div className="">
                                            <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                        </div>

                                    )}
                            </div>


                            {/* <div className="flex flex-wrap items-center gap-y-1 gap-x-2">
                                <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                    Weight Loss Program
                                </span>
                                <span className="hidden sm:inline mx-1" style={{ color: COLORS.TEXT_SECONDARY }}>•</span>
                                <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                    {appointment?.patient.age} years
                                </span>
                                <span className="hidden sm:inline mx-1" style={{ color: COLORS.TEXT_SECONDARY }}>•</span>
                                <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                    {appointment?.patient.gender}
                                </span>
                            </div> */}

                            <div className="flex flex-col w-full text-left pb-1">

                                <div className="flex justify-between w-[80%]">
                                    <div className="text-[#464B55] text-sm">
                                        Submitted:
                                        <p className="text-[#1A202C] text-sm">{appointment?.meetingCreatedAt}</p>
                                    </div>
                                    <div className="text-[#464B55] text-sm">
                                        Program:
                                        <p className="text-[#1A202C] text-sm">Urgent Care</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Patient log button */}
                        {/* <Button className="button-outline !shadow-none w-full sm:w-auto mt-2 sm:mt-0">
                            <Link to="/dashboard/$program/patients/patient/$id" params={{
                                id: appointment?.patient?.patient_id || "",
                                program
                            }}
                                search={{
                                    name: appointment?.patient?.name.replace(/\s+/g, "") || ""
                                }}>
                                View Patient Log
                            </Link>
                        </Button> */}
                    </div>

                    {/* Patient information rows */}
                    <div className="flex flex-col gap-3 sm:mt-3 w-full border rounded-md p-3">
                        {infoRows.map(({ icon, value, title, testId }) => (
                            <InfoRow
                                key={testId}
                                icon={icon}
                                value={value ?? ""}
                                title={title}
                                testId={testId}
                            />
                        ))}
                    </div>

                    <div className="flex gap-4 w-full flex-wrap">
                        {(encounter.status === "happening" || encounter.status === "past" || encounter.status === "cancelled") && (
                            <div className="flex flex-col w-full">
                                <AppointmentStatusCard
                                    status={encounter.status}
                                    encounter={encounter}
                                    onStatusChange={handleMarkAppointmentStatus}
                                />
                            </div>
                        )}
                    </div>

                    {/* Creation date */}
                    {/* <h2 className="text-xs font-normal text-[#202020CC]">Created {appointment?.meetingCreatedAt}</h2> */}
                    {/* <PatientDetailsConditional encounter={encounter} program={program} /> */}

                    {new Date(`${encounter.date} ${encounter.time}`) > new Date() && (
                        <div className="flex flex-col md:flex-row items-center gap-3 p-4 w-full bg-[#EBF0FB] rounded-lg">
                            <Info className="size-6 text-[#4272DD]" />
                            {encounter.status === "happening" ? (
                                <p className="text-sm font-normal text-[#202020CC]">This Appointment is live.</p>
                            ) : (
                                <p className="text-xs font-normal text-[#202020CC] whitespace-normal">This appointment is scheduled for {`${encounter.date}`}. Please ensure you're available 5 minutes before the start time. </p>
                            )}

                            {encounter.status === "happening" && (<Link
                                className={cn(buttonVariants({ variant: "outline" }), "bg-transparent w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border-none py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                to="/dashboard/$program/patients/patient/$id"
                                params={{
                                    id: encounter?.patient.id || "",
                                    program
                                }}
                                search={{
                                    name: encounter?.patient.name.replace(/\s+/g, "") || "",
                                    sessionId: encounter?.session?.session_id || ""
                                }}
                            >
                                View Patient Log
                            </Link>)}

                            <a href={appointment?.meetingLink}
                                target="_blank" rel="noopener noreferrer"
                                className={cn(buttonVariants({ variant: "default" }), "bg-[#4272DD] rounded-lg w-full sm:w-auto", encounter.status !== "happening" ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : "")}>
                                Join meeting
                            </a>
                        </div>
                    )}

                    {(!encounter.type || encounter.type !== "appointment" || (encounter.type === "appointment" && encounter.is_consultation === true)) && <UrgentCareTaskButtons encounter={encounter} />}

                    {program === "urgent-care" && (() => {
                        const sessionData = encounter.session as Session | undefined;
                        const diseaseName = primaryDiagnosis?.disease_name?.toLowerCase?.() ?? "";
                        const isSTI = diseaseName.includes('chlamydia') ||
                            diseaseName.includes('gonorrhea') ||
                            diseaseName.includes('herpes') ||
                            diseaseName.includes('trichomonas') ||
                            diseaseName.includes('bacterial vaginosis') ||
                            diseaseName.includes('yeast infection') ||
                            diseaseName.includes('vulvovaginal candidiasis') ||
                            diseaseName.includes('sti') ||
                            diseaseName.includes('std') ||
                            diseaseName.includes('urethritis') ||
                            diseaseName.includes('sexually transmitted');

                        const toEntries = (questions: unknown) => {
                            if (!questions || typeof questions !== "object" || questions === null) {
                                return [];
                            }

                            return Object.entries(questions as Record<string, string | null>);
                        };

                        const intakeQuestionEntries = toEntries(sessionData?.intake_questions);
                        const redFlagQuestionEntries = toEntries(sessionData?.red_flag_questions);

                        const formatAnswer = (answer: unknown) => {
                            if (typeof answer === "string") {
                                return answer.trim().length > 0 ? answer : "No response provided";
                            }

                            if (answer === null || answer === undefined) {
                                return "No response provided";
                            }

                            try {
                                return String(answer);
                            } catch {
                                return "No response provided";
                            }
                        };

                        const additionalQuestion = typeof primaryDiagnosis?.treatment_page?.additional_question === "string"
                            ? primaryDiagnosis.treatment_page.additional_question
                            : null;

                        const imageUrls: string[] = Array.isArray(primaryDiagnosis?.image_urls)
                            ? primaryDiagnosis.image_urls.filter((url: unknown): url is string => typeof url === "string" && url.length > 0)
                            : [];

                        const shouldShowStiExtras = isSTI && (additionalQuestion || imageUrls.length > 0);
                        const shouldShowInsightsCard = intakeQuestionEntries.length > 0 || redFlagQuestionEntries.length > 0 || shouldShowStiExtras;

                        if (!shouldShowInsightsCard) {
                            return null;
                        }

                        return (
                            <div className="w-full bg-[#FEF3C7] p-4 rounded-[8px] border border-[#FDE68A] mt-3">
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-col space-y-1">
                                        <p className="text-[#1A202C] font-semibold">General Questions</p>
                                        {intakeQuestionEntries.length > 0 ? (
                                            <ul className="text-[#464B55] text-[14px] list-disc ml-4 space-y-1">
                                                {intakeQuestionEntries.map(([question, answer], index) => (
                                                    <li key={`${question}-${index}`}>
                                                        <span className="font-medium text-[#1A202C]">{question}</span>
                                                        {": "}
                                                        <span>{formatAnswer(answer)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-[#464B55] text-[14px]">No general questions available</p>
                                        )}
                                    </div>

                                    <div className="flex flex-col space-y-1">
                                        <p className="text-[#1A202C] font-semibold">Red flag Questions</p>
                                        {redFlagQuestionEntries.length > 0 ? (
                                            <ul className="text-[#464B55] text-[14px] list-disc ml-4 space-y-1">
                                                {redFlagQuestionEntries.map(([question, answer], index) => (
                                                    <li key={`${question}-${index}`}>
                                                        <span className="font-medium text-[#1A202C]">{question}</span>
                                                        {": "}
                                                        <span>{formatAnswer(answer)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-[#464B55] text-[14px]">No red flag questions available</p>
                                        )}
                                    </div>

                                    {shouldShowStiExtras && (
                                        <>
                                            <hr className="border-[#FDE68A]" />
                                            <div className="flex flex-col space-y-1">
                                                <p className="text-[#1A202C] font-semibold">Additional information</p>
                                                <p className="text-[#464B55] text-[14px]">
                                                    {additionalQuestion || "No additional information available"}
                                                </p>
                                            </div>

                                            <hr className="border-[#FDE68A]" />
                                            <div className="flex flex-col space-y-1">
                                                <p className="text-[#1A202C] font-semibold">Image attachment</p>
                                                {imageUrls.length > 0 ? (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {imageUrls.map((url: string, index: number) => (
                                                            <a
                                                                key={index}
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-[#4272DD] text-[14px] underline hover:text-[#2E5FCC]"
                                                            >
                                                                Image {index + 1}
                                                            </a>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-[#464B55] text-[14px]">No images attached</p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {["happening", "past", "completed", "attended"].includes(encounter.status) && encounter.patient.urgent_care_onboarded && (
                        <NotesContainer
                            encounterId={encounter.encounterId}
                            patientId={encounter.patient.id}
                            notes={encounter.notes}
                            icd={encounter.session?.icd10_code}
                        />
                    )
                    }

                    <div className="w-full flex flex-col gap-4 bg-[#EBF0FB] p-4 rounded-[8px] mt-3">
                        <div className="flex flex-col space-y-1 flex-1">
                            <p className="text-[#1A202C] font-semibold">Reported Symptoms</p>
                            <p className="text-[#464B55] text-[14px]">
                                {reportedSymptom || "No reported symptoms"}
                            </p>
                        </div>

                        <hr />

                        <div className="flex flex-col space-y-1 flex-1">
                            <p className="text-[#1A202C] font-semibold">Alfie's Diagnosis</p>
                            <p className="text-[#464B55] text-[14px]">
                                {primaryDiagnosis?.disease_name && primaryDiagnosis?.overview ? (
                                    <>
                                        <span className="font-semibold">{primaryDiagnosis.disease_name}</span>
                                        {" - "}
                                        {primaryDiagnosis.overview}
                                    </>
                                ) : (
                                    "No diagnosis available"
                                )}
                            </p>
                        </div>

                        <hr />

                        <div className="flex flex-col space-y-3 flex-1">
                            <p className="text-[#1A202C] font-semibold">Alfie's Recommended treatment</p>

                            {diagnosisMeds.length > 0 ? (
                                <div className="space-y-4">
                                    {diagnosisMeds.map((medication: any, medIndex: number) => (
                                        <div key={medIndex} className="rounded-md p-3">
                                            {medication?.medication && (
                                                <div>
                                                    <p className="text-[#464B55] font-semibold text-[14px] mb-1">Medication</p>
                                                    <h4 className="text-gray-700 mb-2">{medication.medication}</h4>
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-[#464B55] font-semibold text-[14px] mb-1">Instructions</p>
                                                {Array.isArray(medication?.instructions) && medication.instructions.length > 0 ? (
                                                    <ul className="list-disc list-inside space-y-1 text-[#464B55] text-[14px]">
                                                        {medication.instructions.map((instruction: string, index: number) => (
                                                            <li key={index}>{instruction}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-[#464B55] text-[14px]">No instructions provided</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-white rounded-md p-3 border border-gray-200">
                                    <p className="text-[#464B55] text-[14px]">No medications assigned</p>
                                </div>
                            )}
                        </div>

                        {/* <Link
                            className={cn(buttonVariants({ variant: "outline" }), "w-full h-[44px] rounded-[10px] bg-[#F8FAFF] text-[#4272DD] border border-[#4272DD] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                            to="/dashboard/$program/patients/patient/$id"
                            params={{
                                program,
                                id: encounter.patient.id
                            }}
                            search={{
                                name: encounter.patient.name.replace(/\s+/g, ""),
                                sessionId: encounter?.session?.session_id || ""
                            }}
                        >
                            Go to encounter session
                        </Link> */}
                        <Link
                            className={cn(
                                buttonVariants({ variant: "outline" }),
                                "w-full h-[44px] text-[#4272DD] rounded-[10px] bg-[#F8FAFF] py-3 px-4 hover:bg-[#4272DD] hover:text-white border border-[#4272DD]"
                            )}
                            to="/dashboard/$program/patients/patient/$id"
                            params={{
                                program,
                                id: encounter.patient.id,
                            }}
                            search={{
                                name: (encounter as any).patient.name.replace(/\s+/g, ""),
                                sessionId: (encounter.type === "appointment" && !(encounter as any).is_consultation && (encounter as any)?.session?.session_id) ? (encounter as any).session.session_id : ""
                            }}
                        // onClick={handleReviewPatientLog}
                        >
                            {encounter.type === "appointment" && !(encounter as any).is_consultation ? "Go to encounter session" : "Review Patient Log"}
                        </Link>

                        {(!encounter.type || encounter.type !== "appointment" || (encounter.type === "appointment" && encounter.is_consultation === true)) && (
                            <div className="flex flex-col gap-3 w-full text-left rounded-[16px] border border-[#0404041A] pb-4 px-4 pt-4">
                                <h2 className="font-medium text-[#1A202C]">Assigned actions</h2>

                                {hasAssignedActions ? (
                                    <div className="space-y-4">
                                        {tasks.map((task: any, index: number) => {
                                            const isMedicationTask = task?.type === "medication_payment";
                                            const meds = task?.prescription?.medication ?? [];
                                            const isTaskCompleted = task?.status === "completed";

                                            return (
                                                <div key={task?.task_id || index} className="bg-[#F7F8FA] rounded-[8px] p-4">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div className="flex items-center gap-3">
                                                            {isTaskCompleted ? (
                                                                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                                                    <Check className="w-3 h-3 text-white" />
                                                                </div>
                                                            ) : (
                                                                <Circle className="w-5 h-5 text-blue-500" />
                                                            )}
                                                            <div>
                                                                <h3
                                                                    className={cn(
                                                                        "font-medium",
                                                                        isTaskCompleted ? "text-green-600" : "text-[#1A202C]"
                                                                    )}
                                                                >
                                                                    {getTaskTitle(task)}
                                                                </h3>
                                                                <p className="text-sm text-[#6B7280]">
                                                                    Assigned: {formatTaskDate(task?.created_at)}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {!isMedicationTask && (
                                                            <button
                                                                onClick={() => handleDeleteTask(task?.task_id)}
                                                                disabled={deleteTaskMutation.isPending}
                                                                className="p-1"
                                                            >
                                                                {deleteTaskMutation.status === "pending" ? (
                                                                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                                                ) : (
                                                                    <Trash2 className="w-5 h-5 text-[#6B7280] cursor-pointer hover:text-red-500" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {isMedicationTask && Array.isArray(meds) && meds.length > 0 && (
                                                        <div className="space-y-3">
                                                            {meds.map((m: any, i: number) => (
                                                                <div
                                                                    key={`${m?.name || "med"}-${i}`}
                                                                    className="bg-white rounded-md p-2 space-y-2"
                                                                >
                                                                    <h4 className="font-medium text-[#1A202C]">
                                                                        {m?.name || "-"}
                                                                    </h4>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#6B7280]">
                                                                        <div>
                                                                            <span className="font-medium">Dosage: </span>
                                                                            {m?.dosage ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Duration: </span>
                                                                            {typeof m?.duration === "number" ? `${m.duration} days` : (m?.duration ?? "-")}
                                                                        </div>
                                                                        <div className="md:col-span-2">
                                                                            <span className="font-medium">Instructions: </span>
                                                                            {Array.isArray(m?.instructions) ? (
                                                                                <ul className="list-disc ml-5">
                                                                                    {m.instructions.map((ins: string, idx: number) => (
                                                                                        <li key={idx}>{ins}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            ) : (
                                                                                <span>{m?.instructions ?? "-"}</span>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Refills: </span>
                                                                            {m?.refills ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Effective date: </span>
                                                                            {formatTaskDate(m?.effective_date)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {!isMedicationTask && (
                                                        <div className="text-sm text-[#6B7280] bg-white rounded-md p-2 space-y-2">
                                                            <div>
                                                                <span className="font-medium">Price: </span>
                                                                <span>{task?.cost ?? "-"}</span>
                                                            </div>
                                                            {task?.instructions && (
                                                                <div>
                                                                    <span className="font-medium">Instructions: </span>
                                                                    <span>{task.instructions}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="bg-[#F7F8FA] rounded-[8px] py-12 px-6 flex flex-col items-center justify-center text-center">
                                        <p className="font-medium text-[#1A202C] mb-1">No assigned actions</p>
                                        <p className="text-[#6B7280] text-sm">You haven't assigned any actions on this session yet</p>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>

                </div>
            )}
        </>
    );
};

